import { useMemo, useState, useCallback, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { FileTree } from "src/components/FileTree/FileTree";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { SvgIcon, Grid, Typography, Button, styled } from "@mui/material";
import { IconButton } from "src/components/IconButton/IconButton";
import FiletreeIcon from "../../components/FileTree/filetree-icon.svg?react";
import FiletreeIconOpen from "../../components/FileTree/filetree-icon-open.svg?react";
import FiletreeIconOpenPartial from "../../components/FileTree/filetree-icon-open-partial.svg?react";
import classNames from "classnames";
// Redux
import { insertNewFolder, insertNewFile } from "src/redux/actions/FlightBuilder.Actions";

interface IFileTreeContainerProps {
  fileTreeOpen: boolean;
  toggleFileTreeDrawer: () => void;
}

const OCFileTreeContainer = styled(Grid)(({ theme }) => ({
  height: "100%",
  "& .filetree_header": {
    display: "flex",
    flexDirection: "column",
    width: "238px",
    margin: "10px 0px",
    "& .filetree_title": {
      display: "flex",
      alignItems: "center",
      "& .toggle_drawer_button": {
        width: "32px",
        height: "32px",
        margin: "5px",
      },
    },
    "& .filetree_buttons": {
      display: "flex",
      alignItems: "center",
      "& button": { margin: "5px" },
    },
  },
  "& .filetree_contents": {
    flexGrow: 1,
    overflowY: "auto",
    overflowX: "hidden",
    "& .no_templates": {
      margin: "0px 10px",
      fontSize: 14,
      fontWeight: 300,
      color: theme.custom.forms.inputText,
      maxWidth: "700px",
      whiteSpace: "normal",
    },
    "& .contextMenuAnchor": {
      width: "100%",
      height: "50px",
      top: "30px",
      position: "relative",
    },
  },
}));

/**
 * Save file browser structure in local storage
 * @param structure -
 */
const saveFolderStructure = (structure: string[]): void => {
  localStorage.setItem("template_browser_expanded", JSON.stringify(structure));
};

export const FileTreeContainer = (props: IFileTreeContainerProps): JSX.Element => {
  const dispatch = useDispatch();

  const templates = useSelector((state: IRootState) => state.flightBuilderTemplates.templates);

  // Create a folder node id array so that it can be used for the expand/collapse all functionality.
  const folderNodeIdArray = useMemo(() => {
    const templateFolders = templates?.filter((template) => !template?.isLeafNode).map((template) => template?._id);
    return ["rootNode", ...templateFolders];
  }, [templates]);

  // Will now default to expanded mode showing all content.
  const [expanded, setExpanded] = useState<string[]>(["rootNode"]);
  useEffect(() => {
    try {
      const savedExpand: string[] = JSON.parse(localStorage.getItem("template_browser_expanded") ?? "");
      const updatedExpand = [...savedExpand];
      if (savedExpand?.length) {
        savedExpand.forEach((item, index) => {
          if (!folderNodeIdArray.includes(item)) {
            updatedExpand.splice(index, 1); // remove nodes from expanded if not present in folder node array.
          }
        });
        setExpanded(updatedExpand);
      }
    } catch {
      // do nothing.
    }
  }, [folderNodeIdArray]);

  // If templates are updated, check expanded array and clear out any items that no longer apply.
  useEffect(() => {
    const updatedExpand = [...expanded];
    expanded.forEach((item, index) => {
      if (!folderNodeIdArray.includes(item)) {
        updatedExpand.splice(index, 1); // remove from expanded if not present in folder node array.
      }
    });
    // only update expanded if count changes.
    if (updatedExpand?.length !== expanded?.length) {
      setExpanded(updatedExpand);
    }
  }, [expanded, folderNodeIdArray]);

  const expandToggleIcon = useMemo(() => {
    return folderNodeIdArray?.length === expanded?.length ? (
      <FiletreeIconOpen />
    ) : expanded?.length === 0 ? (
      <FiletreeIcon />
    ) : (
      <FiletreeIconOpenPartial />
    );
  }, [folderNodeIdArray, expanded]);

  /** New Expand/Collapse All nodes feature */
  const handleToggleExpandAll = useCallback(() => {
    setExpanded((prev) => {
      const newExpand = prev.length === folderNodeIdArray?.length ? ["rootNode"] : folderNodeIdArray;
      saveFolderStructure(newExpand);
      return newExpand;
    });
  }, [folderNodeIdArray]);

  const handleToggleExpandFolder = useCallback((event: any, nodeId: any) => {
    saveFolderStructure(nodeId);
    setExpanded(nodeId);
  }, []);

  const newFolder = (): void => {
    dispatch(insertNewFolder());
  };

  const newFile = (): void => {
    dispatch(insertNewFile());
  };

  return (
    <OCFileTreeContainer container direction="column" wrap="nowrap">
      <Grid item className="filetree_header">
        <div className="filetree_title">
          <IconButton
            id="toggle_filetree_btn"
            className="toggle_drawer_button"
            size="small"
            onClick={handleToggleExpandAll}
          >
            <SvgIcon>{expandToggleIcon}</SvgIcon>
          </IconButton>
          <Typography className="toggle_drawer_text" variant="h6" color="textPrimary">
            Template Browser
          </Typography>
        </div>
        <div className={classNames("filetree_buttons")}>
          <Button
            id={"add_folder_btn"}
            className="new_folder"
            onClick={newFolder}
            size="small"
            variant="outlined"
            color="primary"
          >
            Add Folder
          </Button>
          <Button
            id={"add_template_btn"}
            className="new_file"
            onClick={newFile}
            size="small"
            variant="outlined"
            color="primary"
          >
            Add Template
          </Button>
        </div>
      </Grid>

      <Grid item className={classNames("filetree_contents")}>
        {!templates || Object.keys(templates).length === 0 ? (
          <div className="no_templates">
            No Templates created yet, use the buttons above or the form to your right to create and save a recurring
            flight template.
          </div>
        ) : (
          <FileTree expanded={expanded} handleToggleExpandFolder={handleToggleExpandFolder} />
        )}
      </Grid>
    </OCFileTreeContainer>
  );
};
